import { ThemedArea, ErrorBoundary, Skeleton, DeckVisibility } from '@papan/core-ui/ui'
import { useWorkspaceManager } from './WorkspaceManagerContext'
import { WorkspaceView } from './WorkspaceView'

interface WorkspaceCacheProps {
  tenantId: string
  tenantName?: string
  activeWorkspaceId: string | null
  isWorkspaceView: boolean
  userRole?: string
  orgEmail?: string
}

export function WorkspaceCache({ tenantId, tenantName, activeWorkspaceId, isWorkspaceView, userRole, orgEmail }: WorkspaceCacheProps) {
  const { openWorkspaces, loaded, loadFailed, retryLoad, unresolvedFolderIds, transientFailedFolderIds } = useWorkspaceManager()

  const activeExists = openWorkspaces.some(ws => ws.folderId === activeWorkspaceId)
  // Issue #107: only treat the active workspace as "missing" once the
  // list is authoritative. When the boot load gave up (`loadFailed`),
  // `loaded` is true but the list is NOT trustworthy — render a
  // recoverable state, never the "deleted / moved to bin" terminal copy
  // (a transient 404/401/5xx must not be reported to the user as data
  // loss).
  const showMissing = Boolean(isWorkspaceView && activeWorkspaceId && loaded && !activeExists)
  // #675: the active folder's per-folder resolve failed transiently — recoverable
  // exactly like the boot-list give-up, but scoped so it can't stick across
  // folders. Either source renders the recoverable "Couldn't load / Retry" state.
  const transientFailed = Boolean(activeWorkspaceId && transientFailedFolderIds.has(activeWorkspaceId))
  const recoverable = loadFailed || transientFailed
  const showLoadFailed = showMissing && recoverable
  // Absent from the list is NOT evidence of deletion. Opening one from Drive
  // changes the route first and registers the workspace a round trip later, so
  // this used to flash the terminal "deleted / moved to bin" copy on every
  // single open. The terminal state now needs a resolution attempt to have
  // actually failed; until then the open is simply in progress. A recoverable
  // (transient) failure always wins over the terminal copy.
  const resolutionFailed = Boolean(activeWorkspaceId && unresolvedFolderIds.has(activeWorkspaceId))
  const showNotFound = showMissing && !recoverable && resolutionFailed
  const showOpening = showMissing && !recoverable && !resolutionFailed

  return (
    <>
      {openWorkspaces.map(ws => {
        const isActive = isWorkspaceView && ws.folderId === activeWorkspaceId
        return (
          <ThemedArea
            key={ws.folderId}
            area="workspace"
            className="flex-1 flex min-w-0 ws-clear"
            style={{ display: isActive ? undefined : 'none' }}
          >
            <ErrorBoundary>
              {/* #1259: a cached workspace's live reactions wait until it is shown (`useWhenShown`) */}
              <DeckVisibility active={isActive}>
              <WorkspaceView
                folderId={ws.folderId}
                tenantId={tenantId}
                tenantName={tenantName}
                isActive={isActive}
                userRole={userRole}
                orgEmail={orgEmail}
              />
              </DeckVisibility>
            </ErrorBoundary>
          </ThemedArea>
        )
      })}
      {showLoadFailed && (
        <ThemedArea area="workspace" className="flex-1 flex items-center justify-center min-w-0 ws-clear">
          <div className="text-center space-y-3">
            <p className="text-lg themed-text">Couldn’t load this workspace</p>
            <p className="text-sm themed-text-muted">
              A temporary problem prevented loading — your workspace and its files are safe.
            </p>
            <button
              type="button"
              onClick={retryLoad}
              className="themed-btn-primary px-3 py-1.5 text-sm rounded"
            >
              Retry
            </button>
          </div>
        </ThemedArea>
      )}
      {showOpening && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 ws-clear" aria-busy="true">
          {/* Traces the workspace's own three-column shape (explorer · editor ·
              assistant) so the real view lands on top of it instead of pushing
              it aside — the point of a skeleton over a spinner. */}
          <div className="flex-1 flex min-w-0 gap-2 p-2">
            <div className="w-[264px] shrink-0 flex flex-col gap-2 rounded-2xl themed-bg-primary border themed-border p-3">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-8 w-full rounded-lg" />
              <div className="flex flex-col gap-1.5 pt-1">
                {['w-[70%]', 'w-[55%]', 'w-[62%]', 'w-[48%]'].map((w, i) => (
                  <Skeleton key={i} className={`h-4 rounded ${w}`} />
                ))}
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="flex gap-2">
                <Skeleton className="h-9 w-40 rounded-lg" />
                <Skeleton className="h-9 w-9 rounded-lg" />
              </div>
              <div className="flex-1 rounded-2xl themed-bg-primary border themed-border p-4 flex flex-col gap-2.5">
                <Skeleton className="h-4 w-1/3 rounded" />
                {['w-[92%]', 'w-[78%]', 'w-[85%]', 'w-[60%]', 'w-[88%]', 'w-[40%]'].map((w, i) => (
                  <Skeleton key={i} className={`h-3.5 rounded ${w}`} />
                ))}
              </div>
            </div>
          </div>
        </ThemedArea>
      )}
      {showNotFound && (
        <ThemedArea area="workspace" className="flex-1 flex items-center justify-center min-w-0 ws-clear">
          <div className="text-center space-y-2">
            <p className="text-lg themed-text">Project not found</p>
            <p className="text-sm themed-text-muted">This project may have been deleted or moved to bin.</p>
          </div>
        </ThemedArea>
      )}
    </>
  )
}
